import React, { useEffect, useState } from 'react';
import { Row, Col, Form } from "react-bootstrap";
import { MdOutlinePaid, MdOutlineBed, MdOutlineFactCheck, MdPersonOutline } from "react-icons/md";
import { fetchAllStudents } from '../reducers/StudentSlice';
import { fetchAllRooms } from '../reducers/RoomSlice';
import { useDispatch, useSelector } from 'react-redux';
import { getAllBookings } from '../reducers/BookingSlice';
import { fetchAllStaffMembers } from '../reducers/StaffSlice';
import { fetchAllWorkers } from '../reducers/WorkerSlice';
import { fetchAllSalaries } from '../reducers/SalarySlice';
import { fetchAllMaintenances } from '../reducers/MaintenanceSlice';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { students } = useSelector((state) => state.students);
  const { rooms } = useSelector((state) => state.rooms);
  const { bookings } = useSelector((state) => state.bookings);
  const { staffMembers } = useSelector((state) => state.staffs);
  const { workers } = useSelector((state) => state.workers);
  const { salaries } = useSelector((state) => state.salaries);
  const { maintenances } = useSelector((state) => state.maintenances);
  const [selectedPaymentModeIncome, setSelectedPaymentModeIncome] = useState('all');
  const [selectedPaymentModeSecurity, setSelectedPaymentModeSecurity] = useState('all');

  useEffect(() => {
    dispatch(fetchAllStudents());
    dispatch(fetchAllRooms());
    dispatch(getAllBookings());
    dispatch(fetchAllWorkers());
    dispatch(fetchAllStaffMembers());
    dispatch(fetchAllSalaries());
    dispatch(fetchAllMaintenances());
  }, [dispatch]);

  const totalIncomeByMode = (paymentMode) => {
    return bookings?.reduce((total, booking) => {
      if (paymentMode === 'all') {
        return total + (booking?.paymentHistory?.reduce((sum, payment) => sum + (payment?.amount || 0), 0) || 0);
      }
      if (booking.paymentMode === paymentMode) {
        return total + (booking?.paymentHistory?.reduce((sum, payment) => sum + (payment?.amount || 0), 0) || 0);
      }
      return total;
    }, 0);
  };

  const securityChargesByMode = (paymentMode) => {
    return bookings?.reduce((total, booking) => {
      if (paymentMode === 'all') {
        return total + (booking.securityCharges || 0);
      }
      return total + (booking.securityPaymentMode === paymentMode ? booking.securityCharges || 0 : 0);
    }, 0);
  };

  const handlePaymentModeIncomeChange = (e) => {
    setSelectedPaymentModeIncome(e.target.value);
  };

  const handlePaymentModeSecurityChange = (e) => {
    setSelectedPaymentModeSecurity(e.target.value);
  };

  const totalIncome = totalIncomeByMode(selectedPaymentModeIncome);
  const selectedSecurityCharges = securityChargesByMode(selectedPaymentModeSecurity);

  const totalNetSalary = salaries?.reduce((total, salary) => {
    const historySum = salary.salaryHistory?.reduce((sum, record) => sum + record.netSalary, 0);
    return total + (historySum || 0);
  }, 0);

  const totalMaintenanceCost = maintenances?.reduce((sum, item) => sum + (item.totalCost || 0), 0);

  return (
    <div className='container-fluid dashboard'>
      <Row className="my-1 text-white">
        <h5 className='mb-3'>Overview</h5>

        <Col md={3} className="dash-boxes mb-4">
          <Form.Select
            className="mb-3"
            value={selectedPaymentModeIncome}
            onChange={handlePaymentModeIncomeChange}
          >
            <option value="all">All</option>
            <option value="cash">Cash</option>
            <option value="bank_transfer">Bank Transfer</option>
          </Form.Select>
          <div className="content border rounded d-flex justify-content-between p-4 bg-primary">
            <div className="text-box text-white">
              <h6>Total Income</h6>
              <span>{totalIncome?.toLocaleString()} PKR</span>
            </div>
            <div className="icon-box d-flex align-items-center">
              <MdOutlinePaid size={80} />
            </div>
          </div>
        </Col>

        <Col md={3} className="dash-boxes mb-4">
          <div className="content border rounded d-flex justify-content-between p-4 bg-warning">
            <div className="text-box text-white">
              <h6>Total Customers</h6>
              <span>{students?.length} Customers</span>
            </div>
            <div className="icon-box d-flex align-items-center">
              <MdPersonOutline size={80} />
            </div>
          </div>
        </Col>

        <Col md={3} className="dash-boxes mb-4">
          <div className="content border rounded d-flex justify-content-between p-4 bg-success">
            <div className="text-box text-white">
              <h6>Total Rooms</h6>
              <span>{rooms?.length} Rooms</span>
            </div>
            <div className="icon-box d-flex align-items-center">
              <MdOutlineBed size={80} />
            </div>
          </div>
        </Col>

        <Col md={3} className="dash-boxes mb-4">
          <div className="content border rounded d-flex justify-content-between p-4 bg-secondary">
            <div className="text-box text-white">
              <h6>Total Bookings</h6>
              <span>{bookings?.length} Bookings</span>
            </div>
            <div className="icon-box d-flex align-items-center">
              <MdOutlineFactCheck size={80} />
            </div>
          </div>
        </Col>

        <Col md={3} className="dash-boxes mb-4">
          <Form.Select
            className="mb-3"
            value={selectedPaymentModeSecurity}
            onChange={handlePaymentModeSecurityChange}
          >
            <option value="all">All</option>
            <option value="cash">Cash</option>
            <option value="bank_transfer">Bank Transfer</option>
          </Form.Select>
          <div className="content border rounded d-flex justify-content-between p-4 bg-info">
            <div className="text-box text-white mb-3">
              <h6>Security Charges</h6>
              <span>{selectedSecurityCharges?.toLocaleString()} PKR</span>
            </div>
            <div className="icon-box d-flex align-items-center">
              <MdOutlinePaid size={80} />
            </div>
          </div>
        </Col>

        <Col md={3} className="dash-boxes mb-4">
          <div className="content border rounded d-flex justify-content-between p-4 bg-secondary">
            <div className="text-box text-white">
              <h6>Total Staffs</h6>
              <span>{staffMembers?.length} Staffs</span>
            </div>
            <div className="icon-box d-flex align-items-center">
              <MdPersonOutline size={80} />
            </div>
          </div>
        </Col>

        <Col md={3} className="dash-boxes mb-4">
          <div className="content border rounded d-flex justify-content-between p-4 bg-primary">
            <div className="text-box text-white">
              <h6>Total Workers</h6>
              <span>{workers?.length} Workers</span>
            </div>
            <div className="icon-box d-flex align-items-center">
              <MdPersonOutline size={80} />
            </div>
          </div>
        </Col>

        <Col md={3} className="dash-boxes mb-4">
          <div className="content border rounded d-flex justify-content-between p-4 bg-danger">
            <div className="text-box text-white">
              <h6>Paid Salaries</h6>
              <span>{totalNetSalary?.toLocaleString()} PKR</span>
            </div>
            <div className="icon-box d-flex align-items-center">
              <MdOutlinePaid size={80} />
            </div>
          </div>
        </Col>

        <Col md={3} className="dash-boxes mb-4">
          <div className="content border rounded d-flex justify-content-between p-4 bg-warning">
            <div className="text-box text-white">
              <h6>Maintenance Cost</h6>
              <span>{totalMaintenanceCost?.toLocaleString()} PKR</span>
            </div>
            <div className="icon-box d-flex align-items-center">
              <MdOutlinePaid size={80} />
            </div>
          </div>
        </Col>
        
      </Row>
    </div>
  );
};

export default Dashboard;
